import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentsApi, type UpdateOrganizationPaymentSettingsPayload, type UpdatePaymentSettingsPayload } from '@/api/payments';

export const paymentKeys = {
  settings: (competitionId: string) => ['payments', competitionId, 'settings'] as const,
  mine: (competitionId: string) => ['payments', competitionId, 'mine'] as const,
  organization: (organizationId: string) => ['payments', 'organization', organizationId] as const,
};

export function usePaymentSettings(competitionId: string | undefined) {
  return useQuery({
    queryKey: paymentKeys.settings(competitionId ?? ''),
    queryFn: () => paymentsApi.getSettings(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useUpdatePaymentSettings(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePaymentSettingsPayload) => paymentsApi.updateSettings(competitionId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKeys.settings(competitionId), data);
      queryClient.invalidateQueries({ queryKey: paymentKeys.mine(competitionId) });
    },
  });
}

export function useSetEntryFee(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryFeeCents: number | null) => paymentsApi.setEntryFee(competitionId, entryFeeCents),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKeys.settings(competitionId), data);
      queryClient.invalidateQueries({ queryKey: paymentKeys.mine(competitionId) });
    },
  });
}

export function useOrganizationPaymentSettings(organizationId: string | undefined) {
  return useQuery({
    queryKey: paymentKeys.organization(organizationId ?? ''),
    queryFn: () => paymentsApi.getOrganizationSettings(organizationId as string),
    enabled: Boolean(organizationId),
  });
}

export function useUpdateOrganizationPaymentSettings(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOrganizationPaymentSettingsPayload) => paymentsApi.updateOrganizationSettings(organizationId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKeys.organization(organizationId), data);
      // Every competition of the club reads these options.
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

/** What the signed-in competitor owes and the ways to pay it. Polls briefly after a card payment so "paid" appears by itself. */
export function useMyPayment(competitionId: string | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: paymentKeys.mine(competitionId ?? ''),
    queryFn: () => paymentsApi.mine(competitionId as string),
    enabled: Boolean(competitionId),
    retry: false,
    refetchInterval: (query) => (options?.poll && query.state.data && !query.state.data.hasPaid ? 3000 : false),
  });
}

export function useConfirmCheckout(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => paymentsApi.confirm(competitionId, sessionId),
    onSuccess: (data) => queryClient.setQueryData(paymentKeys.mine(competitionId), data),
  });
}

export function useStartCheckout(competitionId: string) {
  return useMutation({ mutationFn: () => paymentsApi.checkout(competitionId) });
}
